import { useCallback, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import StepLayout from './components/StepLayout';
import FormSectionRenderer from './FormSectionRenderer';
import { FORM_STEPS, INITIAL_FORM_VALUES } from './formConfig';
import type { FormalwearFormValues, FormValueKey } from './formTypes';

interface FormProps {
  onSubmit?: (values: FormalwearFormValues) => void;
}

export default function YangbokForm({ onSubmit }: FormProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formValues, setFormValues] = useState<FormalwearFormValues>(INITIAL_FORM_VALUES);

  const step = FORM_STEPS[currentStep];
  const canNext = step.canNext(formValues);

  const setFieldValue = useCallback(
    <K extends FormValueKey>(key: K, value: FormalwearFormValues[K]) => {
      setFormValues((prev) => ({
        ...prev,
        [key]: value,
      }));
    },
    []
  );

  const goNext = useCallback(() => {
    if (!canNext) return;

    if (currentStep < FORM_STEPS.length - 1) {
      setDirection(1);
      setCurrentStep((prev) => prev + 1);
      return;
    }

    setIsSubmitted(true);
    onSubmit?.(formValues);
  }, [canNext, currentStep, formValues, onSubmit]);

  const goBack = useCallback(() => {
    if (currentStep <= 0) return;
    setDirection(-1);
    setCurrentStep((prev) => prev - 1);
  }, [currentStep]);

  const reset = useCallback(() => {
    setCurrentStep(0);
    setDirection(1);
    setIsSubmitted(false);
    setFormValues(INITIAL_FORM_VALUES);
  }, []);

  const variantProps = {
    initial: { opacity: 0, x: direction * 40 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: direction * -40 },
    transition: { duration: 0.35, ease: 'easeOut' },
  };

  if (isSubmitted) {
    return (
      <div className="min-h-[100dvh] bg-cream flex flex-col">
        <div className="flex-1 px-5 flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
            className="w-full rounded-3xl bg-white border border-cream-dark p-7 text-center shadow-lg shadow-navy/8"
            style={{ maxWidth: 500 }}
          >
            <div className="w-14 h-14 rounded-2xl bg-navy mx-auto flex items-center justify-center text-white mb-5">
              <i className="ri-check-line text-2xl" />
            </div>
            <h2 className="font-serif text-xl text-navy leading-relaxed">
              예약 신청 정보가 준비됐어요.
            </h2>
            <p className="text-xs text-navy-light mt-2 leading-relaxed">
              입력하신 내용을 기준으로 상담 가능 업체와 일정을 조율하면 됩니다.
            </p>
            <button
              type="button"
              onClick={reset}
              className="mt-6 w-full rounded-xl bg-navy py-4 text-sm font-semibold text-white cursor-pointer active:scale-[0.99] transition-transform"
            >
              다시 작성하기
            </button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <StepLayout
      step={currentStep + 1}
      totalSteps={FORM_STEPS.length}
      question={step.question}
      description={step.description}
      onBack={goBack}
      onNext={goNext}
      canGoNext={canNext}
      headerTitle="예복 예약 센터"
      finalLabel="예약 신청 완료"
      hideBackOnFirstStep
    >
      <AnimatePresence mode="wait" custom={direction}>
        <motion.div key={step.key} {...variantProps} className="space-y-7">
          {step.sections.map((section) => (
            <FormSectionRenderer
              key={`${step.key}-${section.key}-${section.type}`}
              section={section}
              values={formValues}
              setFieldValue={setFieldValue}
            />
          ))}
        </motion.div>
      </AnimatePresence>
    </StepLayout>
  );
}
